<template>
    <div class="flex flex-col h-full w-full gap-2 px-2">
        <div class="flex flex-col h-0 grow w-full">
            <dx-data-grid
                width="100%"
                height="100%"
                ref="gridRef"
                class="h-0 grow"
                column-resizing-mode="widget"
                :focused-row-enabled="meta?.focusedRowEnabled || editing?.allowAdding"
                :allow-column-reordering="true"
                :allow-column-resizing="true"
                :column-min-width="70"
                :column-auto-width="true"
                :data-source="displayList"
                :hover-state-enabled="true"
                :key-expr="keyExpr"
                :show-borders="true"
                :show-column-lines="true"
                :show-row-lines="true"
                :word-wrap-enabled="meta.wordWrapEnabled"
                :column-chooser="{ enabled: meta?.columnChooserEnabled ?? true, mode: 'select' }"
                :export="{ enabled: true, allowExportSelectedData: true }"
                :filter-panel="{ visible: true }"
                :filter-row="{ visible: true }"
                :fixing="{ enabled: true }"
                :header-filter="{ visible: true }"
                :load-panel="{ enabled: true }"
                :scrolling="{ mode: meta.scrollingMode || 'virtual' }"
                :paging="{ enabled: meta.isPagingEnabled || false }"
                :search-panel="{
                    visible: meta.searchPanelEnabled ?? true,
                    placeholder: 'Поиск...',
                    locateInMenu: 'auto',
                }"
                :selected-row-keys="selectedRowKeys"
                :selection="{
                    mode: meta.selectionMode || 'multiple',
                    showCheckBoxesMode: meta.showCheckBoxesMode || 'always',
                    allowSelectAll: meta.allowSelectAll || false,
                }"
                @saved="saved"
                @selection-changed="selectionChanged"
                @content-ready="contentReady"
                @exporting="onExporting"
                @focused-row-changed="onFocusedRowChanged"
            >
                <dx-editing
                    :allow-adding="editing?.allowAdding || false"
                    :allow-deleting="editing?.allowDeleting || false"
                    :allow-updating="editing?.allowUpdating || false"
                    :use-icons="editing?.useIcons || true"
                    mode="batch"
                />
                <dx-column
                    v-if="editing?.allowAdding"
                    :data-field="'greyEminence'"
                    :caption="'greyEminence'"
                    :data-type="'string'"
                    :visible="false"
                    :showInColumnChooser="false"
                />
                <dx-column
                    v-for="(item, index) in meta.columns"
                    :key="index"
                    :allow-editing="item.allowEditing"
                    :allow-header-filtering="item.allowHeaderFiltering ?? true"
                    :calculate-cell-value="item.calculateCellValue"
                    :calculate-display-value="item.calculateDisplayValue"
                    :caption="item.caption"
                    :cell-template="item.cellTemplate"
                    :data-field="item.dataField"
                    :data-type="item.dataType || 'string'"
                    :edit-cell-template="item.editCellTemplate"
                    :editor-options="item.editorOptions"
                    :group-index="item.groupIndex"
                    :lookup="item.lookup || null"
                    :sort-index="item.sortIndex"
                    :sort-order="item.sortOrder"
                    :validation-rules="item.validationRules"
                    :visible="item.visible"
                    :width="item.width"
                    :showInColumnChooser="item.showInColumnChooser || true"
                />

                <dx-toolbar :height="'64px'">
                    <slot name="toolbarItems"></slot>

                    <slot name="refreshButton"></slot>

                    <slot name="copyButton"></slot>

                    <dx-item location="after" locate-in-menu="auto" v-if="editing?.allowAdding">
                        <dx-button icon="copy" :disabled="copyButtonDisable" @click="onCopyClick" />
                    </dx-item>

                    <slot name="addButton"></slot>

                    <dx-item name="addRowButton" location="after" locate-in-menu="auto" />

                    <dx-item v-if="meta.enableDeleting">
                        <dx-button
                            icon="trash"
                            hint="Удалить"
                            :disabled="deleteButtonDisabled"
                            @click="onDeleteClick"
                        />
                    </dx-item>

                    <slot name="savebutton"></slot>

                    <dx-item name="searchPanel" location="after" locate-in-menu="auto" />
                    <dx-item v-if="meta?.exportEnabled ?? true" location="after">
                        <DxDropDownButton
                            :dropDownOptions="{ width: 350 }"
                            icon='<svg width="32" height="32" viewBox="0 0 76 74" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_1695_4138)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C2.23858 0 0 2.23858 0 5V69C0 71.7614 2.23858 74 5 74H57C59.7614 74 62 71.7614 62 69V21H44C42.3431 21 41 19.6569 41 18V0H5Z" fill="#286EB6"/>
<path d="M45 1L53 9L61 17H48C46.3431 17 45 15.6569 45 14V1Z" fill="#286EB6"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879ZM38.2068 51.2919L52.8038 51.2879ZM52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" fill="white"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879M52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" stroke="#286EB6" stroke-width="4"/>
<path d="M32 41C32 39.8954 32.8954 39 34 39H55V49H34C32.8954 49 32 48.1046 32 47V41Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_1695_4138">
<rect width="76" height="74" fill="white"/>
</clipPath>
</defs>
</svg>'
                        >
                            <dx-item>
                                <div class="ai-dx-drop-down-button-item">Экспортировать все в pdf</div>
                            </dx-item>
                            <dx-item>
                                <div class="ai-dx-drop-down-button-item">Экспортировать выбранные строки в pdf</div>
                            </dx-item>
                            <dx-item>
                                <div class="ai-dx-drop-down-button-item">Экспортировать всё в EXCEL</div>
                            </dx-item>
                            <dx-item>
                                <div class="ai-dx-drop-down-button-item">Экспортировать выбранные строки в EXCEL</div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>

                    <dx-item v-if="meta?.settingsEnabled ?? true" location="after" locate-in-menu="auto">
                        <dx-drop-down-button
                            icon='<svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z" fill="#286EB6"/>
                          </svg>'
                            styling-mode="text"
                            displayExpr="name"
                            hint="Пользовательские настройки"
                            :items="[
                                {
                                    id: 1,
                                    name: 'Настройки пользователя',
                                },
                                {
                                    id: 1,
                                    name: 'Заводские настройки',
                                },
                            ]"
                            :dropDownOptions="{ width: 230 }"
                        />
                    </dx-item>

                    <dx-item
                        v-if="meta?.columnChooserEnabled ?? true"
                        widget="dxButton"
                        location="after"
                        show-text="inMenu"
                        locate-in-menu="auto"
                    >
                        <dx-button class="ai-button-base" hint="Выбор столбцов" @click="showColumnChooser">
                            <ai-show-column-chooser-base />
                        </dx-button>
                    </dx-item>
                </dx-toolbar>

                <template v-for="(_, name) in $slots" #[name]="{ data }">
                    <slot :data="data" :name="name" locate-in-menu="auto"></slot>
                </template>
            </dx-data-grid>
        </div>
        <grid-footer ref="childRef" />
    </div>
</template>

<script setup>
import { DxDataGrid, DxColumn, DxToolbar, DxItem, DxEditing } from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { onMounted, ref, computed } from 'vue';

import gridFooter from './grid-footer.vue';
import onExporting from '@/utils/export-to-excel.js';

const props = defineProps({
    meta: {
        type: Object,
        default: () => {
        },
    },
    dataSource: {
        type: Object,
        default: () => {
        },
    },
    selection: {
        type: Object,
        default: () => {
        },
    },
    editing: {
        type: Object,
        default: () => {
        },
    },
    selectedRowKeys: {
        type: Array,
        default: () => [],
    },
});

const gridRef = ref(null);
const gridInstance = ref(null);
const changesRef = ref([]);
const childRef = ref(null);
const totalCount = ref(0);
const deleteButtonDisabled = ref(true);
const copyButtonDisable = ref(true);
const rowsPreparedToDelete = ref([]);
const keyExpr = computed(() =>
    props.editing?.allowAdding || props.editing?.allowCustomAdding ? 'greyEminence' : props.meta.keyExpr,
);

const displayList = computed(() => {
    if (props.editing?.allowAdding || props.editing?.allowCustomAdding) {
        const updatedSource = props.dataSource?.map(item => {
            item.greyEminence = item[props.meta.keyExpr] ?? Math.random();
            return item;
        });
        return updatedSource;
    } else {
        return props.dataSource;
    }
});

const contentReady = () => {
    childRef.value.setTotalCount(gridInstance.value?.getVisibleRows().length);
};

const onFocusedRowChanged = () => {
    copyButtonDisable.value = false;
};

const onCopyClick = async () => {
    const key = gridInstance.value.option('focusedRowKey');
    const newRowData = await gridInstance.value.getDataByKeys([key]);

    const copy = { ...newRowData[0] };
    copy['greyEminence'] = `${Math.random()}`;

    const change = {
        type: 'insert',
        data: copy,
        key: copy['greyEminence'],
        index: 0,
    };

    const changesOptions = [...gridInstance.value.option('editing.changes'), change];

    await gridInstance.value.option('editing.changes', changesOptions);
    gridInstance.value.saveEditData();
    await gridInstance.value.refresh();
};

const addNewOption = newItems => {
    const newOption = {
        data: newItems,
        type: 'insert',
        key: Math.random(),
        insertAfterKey: 2,
    };

    const changesOption = [...gridInstance.value.option('editing.changes')];
    changesOption.push(newOption);
    gridInstance.value.option('editing.changes', changesOption);
};

onMounted(() => {
    gridInstance.value = gridRef.value.instance;
    gridInstance.value.on('optionChanged', event => {
        if (event.name === 'editing' && event.value) {
            changesRef.value = event.value;
        }
    });
});

const emit = defineEmits(['selectionChanged']);

const showColumnChooser = () => {
    gridRef.value.instance.showColumnChooser();
};

const selectionChanged = event => {
    emit('selectionChanged', event);
    childRef.value.selectRows(event.selectedRowKeys.length, totalCount);
    deleteButtonDisabled.value = !event.selectedRowKeys.length;
};

const onDeleteClick = () => {
    const selectedRows = gridInstance.value.getSelectedRowKeys();
    selectedRows.forEach(key => {
        const index = gridInstance.value.getRowIndexByKey(key);
        if (rowsPreparedToDelete.value.includes(key)) {
            gridInstance.value.undeleteRow(index);
            rowsPreparedToDelete.value = rowsPreparedToDelete.value.filter(value => value !== key);
        } else {
            gridInstance.value.deleteRow(index);
            rowsPreparedToDelete.value.push(key);
        }
    });
    gridInstance.value.clearSelection();
};

defineExpose({
    gridInstance,
    changesRef,
    addNewOption,
    onCopyClick,
    onDeleteClick,
});
</script>

<style lang="scss" scoped>
:deep(.dx-row-removed > td) {
    background-color: white;
    text-decoration: line-through;
}
</style>
